import { useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { ComboSelect } from '../../../components/ComboSelect';
import type { PlmLayerRow } from './plm.api';

/**
 * "Carry Over Results" — copy one layer's work onto others (PLM Phase 3).
 *
 * Port of the legacy `PLMCopyToCombo_DetailView` popup: a SOURCE combo listing
 * the layers that have actually been entered, a CopyTo checklist of every other
 * layer, and the Copy / Clear pair. Legacy also puts Copy Previous and Copy To
 * All in this popup, and so do we — all three end up calling the same copy.
 *
 * A copy REPLACES the target's whole layer (observation, exam rows and point
 * count), which is what legacy does, so the dialog says so plainly.
 */

export interface PlmCarryOverDialogProps {
  open: boolean;
  /** Every layer in the batch, in run order. */
  layers: PlmLayerRow[];
  /** The layer the screen is currently on — the default source. */
  currentSequenceOid: string | null;
  /** True for a layer this user may not change (another analyst's, a blind
   *  one, the Daily QC lock) — the server refuses a copy onto it, so it is not
   *  offered as a target. */
  isLocked?: (l: PlmLayerRow) => boolean;
  /** True for a layer blind to this user (the SIMPLE dup flow) — never offered as the
   *  source (owner 2026-10-17, G5 parity: OpenCarryOver); the server refuses it too. */
  isBlind?: (l: PlmLayerRow) => boolean;
  onCancel: () => void;
  /** Runs the copy. `targets` never contains the source. */
  onCopy: (sourceSequenceOid: string, targets: string[]) => void | Promise<void>;
}

export function PlmCarryOverDialog({
  open, layers: allLayers, currentSequenceOid, isLocked, isBlind, onCancel, onCopy,
}: PlmCarryOverDialogProps) {
  // A QC re-read is an INDEPENDENT read: never copied from or onto (the server
  // refuses both, "copy-reread"). Offering them made every copy onto the Intra
  // / Inter row fail (owner report 2026-09-29).
  const layers = useMemo(() => allLayers.filter(l => !l.reReadKind), [allLayers]);
  // Legacy seeds the source combo with the ENTERED layers only — copying a
  // blank layer over a filled one is never what the analyst meant.
  const sourceOptions = useMemo(
    () => layers.filter(l => (l.hasMaterial || l.examCount > 0) && !isBlind?.(l)),
    [layers, isBlind],
  );
  const canTarget = (l: PlmLayerRow) => !isLocked?.(l);

  const [source, setSource] = useState<string>(() => {
    const current = sourceOptions.find(l => l.sequenceOid === currentSequenceOid);
    return current?.sequenceOid ?? sourceOptions[0]?.sequenceOid ?? '';
  });
  const [targets, setTargets] = useState<Set<string>>(new Set());

  /** Everything except the source — legacy removes it from the checklist. */
  const targetOptions = useMemo(
    () => layers.filter(l => l.sequenceOid !== source && !isLocked?.(l)),
    [layers, source, isLocked],
  );

  const label = (oid: string) =>
    layers.find(l => l.sequenceOid === oid)?.sampleL ?? '';

  const toggle = (oid: string) =>
    setTargets(prev => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid); else next.add(oid);
      return next;
    });

  const clear = () => { setTargets(new Set()); };

  /** Copy Previous — the layer immediately before the current one becomes the
   *  source, and the current one the only target (legacy :2302). */
  const copyPrevious = async () => {
    const ordered = [...layers].sort((a, b) => a.sort - b.sort);
    const i = ordered.findIndex(l => l.sequenceOid === currentSequenceOid);
    if (i <= 0 || !canTarget(ordered[i]!)) return;   // guarded by the button's disabled state
    await onCopy(ordered[i - 1]!.sequenceOid, [ordered[i]!.sequenceOid]);
  };

  /** Copy To All — the source onto every other layer (legacy :2361). */
  const copyToAll = async () => {
    if (!source) return;
    await onCopy(source, targetOptions.map(l => l.sequenceOid));
  };

  const copySelected = async () => {
    if (!source || targets.size === 0) return;
    await onCopy(source, [...targets]);
  };

  const ordered = [...layers].sort((a, b) => a.sort - b.sort);
  const currentIndex = ordered.findIndex(l => l.sequenceOid === currentSequenceOid);
  const previousLabel = currentIndex > 0 ? ordered[currentIndex - 1]!.sampleL : null;
  const canCopyPrevious = currentIndex > 0 && canTarget(ordered[currentIndex]!);

  return (
    <AppDialog
      open={open}
      title="Carry Over Results"
      icon="ti-copy"
      onClose={onCancel}
      width={560}
      footer={
        <>
          <button
            type="button"
            className="ca-btn ca-btn-solid"
            disabled={!source || targets.size === 0}
            onClick={() => { void copySelected(); }}
          >
            Copy{targets.size > 0 ? ` (${targets.size})` : ''}
          </button>
          <button type="button" className="ca-btn" onClick={clear}>Clear</button>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
        </>
      }
    >
      {sourceOptions.length === 0 ? (
        <div className="plm-exam__empty">
          No layer has been entered yet, so there is nothing to carry over.
        </div>
      ) : (
        <div className="plm-carry">
          <p className="plm-carry__warn">
            A copy <strong>replaces</strong> the target layer entirely — its
            observation, its component rows and its point count.
          </p>

          <div className="plm-field">
            <label>Source Sample</label>
            <ComboSelect
              value={source}
              options={sourceOptions.map(l => ({ value: l.sequenceOid, label: l.sampleL ?? '' }))}
              onChange={v => { setSource(v); setTargets(new Set()); }}
              placeholder="Select…"
              clearable={false}
            />
          </div>

          <div className="plm-field">
            <label>Copy To</label>
            <div className="plm-carry__list" role="group" aria-label="Copy to layers">
              {targetOptions.map(l => (
                <label key={l.sequenceOid} className="plm-chk">
                  <input
                    type="checkbox"
                    checked={targets.has(l.sequenceOid)}
                    onChange={() => toggle(l.sequenceOid)}
                  />
                  <span>
                    {l.sampleL}
                    {(l.hasMaterial || l.examCount > 0) && (
                      <em className="plm-carry__has-data"> — will be overwritten</em>
                    )}
                  </span>
                </label>
              ))}
              {targetOptions.length === 0 && (
                <div className="plm-exam__empty">No other layer can be copied onto.</div>
              )}
            </div>
          </div>

          <div className="plm-carry__shortcuts">
            <button
              type="button"
              className="ca-btn"
              disabled={!canCopyPrevious}
              title={currentIndex <= 0
                ? 'The first layer has nothing before it'
                : !canCopyPrevious ? 'You cannot change this layer'
                : `Copy ${previousLabel} onto ${label(currentSequenceOid ?? '')}`}
              onClick={() => { void copyPrevious(); }}
            >
              Copy Previous
            </button>
            <button
              type="button"
              className="ca-btn"
              disabled={!source || targetOptions.length === 0}
              title={`Copy ${label(source)} onto every other layer`}
              onClick={() => { void copyToAll(); }}
            >
              Copy To All
            </button>
          </div>
        </div>
      )}
    </AppDialog>
  );
}
